import React, { useEffect, useCallback, useContext, useState, useRef } from 'react';
import confetti from 'canvas-confetti';
import Modal from 'react-bootstrap/Modal';
import Button from "react-bootstrap/Button";
import { useSwipeable } from "react-swipeable";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass2, faTasks } from '@fortawesome/free-solid-svg-icons';

import useSessionStorage from '../hooks/useSessionStorage';
import quizInstanceStatusContext from '../context/quizInstanceStatusContext';
import QuizTimer from './QuizTimer';
import Status from '../enums/QuizInstanceStatus';
import QuizQuestion from './QuizQuestion';
import { isTouchDevice } from '../utils';

const QUIZ_LOW_TIME_THRESHOLD_PERCENT = 10;

export default function Quiz() {

  const ctx = useContext(quizInstanceStatusContext);
  console.log("questionList:", ctx.quizInfo.questionList);
  const { setQuizAnswerData } = ctx;

  const [answerChoiceMap, setAnswerChoiceMap] = useSessionStorage(`${ctx.quizInfo.id}_answer_choices`, {});
  const [answerTextMap, setAnswerTextMap] = useSessionStorage(`${ctx.quizInfo.id}_answer_text`, {});

  const [showLowTimeAlert, setShowLowTimeAlert] = useState(false);
  const [hasDismissedLowTimeAlert, setHasDismissedLowTimeAlert] = useState(false);
  const [celebrationLevel, setCelebrationLevel] = useState(0); // 1 = bra jobbat, 2 = mästare
  const hasTriggeredCelebration = useRef(false);

  const now = new Date();
  const isStandingMode = (ctx.quizInfo.config || {}).standingMode === true;
  const quizIsActive = ctx.status === Status.Joined && (isStandingMode || ctx.quizInfo.endsAt > now);

  // =====================================================
  // Callback that triggers the quiz answers to be saved
  // =====================================================
  const saveQuizAnswers = useCallback(() => {

    // A Quiz may mix choice questions and input-mode (text) questions, so
    // merge both local maps before sending. Free-only quizzes still work
    // because answerChoiceMap is empty in that case.
    const quizAnswerData = { ...answerChoiceMap, ...answerTextMap };

    setAnswerChoiceMap({});
    setAnswerTextMap({});

    setQuizAnswerData(quizAnswerData);

  }, [answerChoiceMap, answerTextMap, setAnswerChoiceMap, setAnswerTextMap, setQuizAnswerData]);

  // ==================================================
  // Callback that updates the local answer choice map 
  // ==================================================
  const onAnswerChoiceToggled = useCallback((question, answer) => {

    if (isStandingMode || ctx.quizInfo.endsAt > new Date())
    {
      setAnswerChoiceMap(curVal => {

        if (!curVal[question.id]) {
          curVal[question.id] = [];
        }

        const answerIdx = curVal[question.id].indexOf(answer.id);
        if (answerIdx === -1) {

          // Reset the array if the user may only give one answer
          if (question.config?.single_answer) {
            curVal[question.id] = [];
          }

          curVal[question.id].push(answer.id);
        }
        else {
          curVal[question.id].splice(answerIdx, 1);
        }

        return {...curVal };
      });
    }

  }, [ctx, isStandingMode, setAnswerChoiceMap]);

  // =================================================
  // Callback that updates the local answer text map
  // =================================================
  const onAnswerTextUpdated = useCallback((question, text) => {

    setAnswerTextMap(curVal => {

      curVal[question.id] = text;
      return {...curVal};
    });

  }, [setAnswerTextMap]);

  // State to track the current swipe question index (horizontal scroll)
  const [swipeQuestionIdx, setSwipeQuestionIdx] = useState(0);

  // =============================================
  // Swipe handler (if horizontal scroll enabled)
  // =============================================
  const { ref: documentRef } = useSwipeable({
    onSwiped: ({ dir, event }) => {
      if (dir === "Left") {
        setSwipeQuestionIdx(idx => idx < ctx.quizInfo.questionList.length - 1 ? idx + 1 : idx);
      } else if (dir === "Right") {
        setSwipeQuestionIdx(idx => idx > 0 ? idx - 1 : idx);
      }
    },
    preventDefaultTouchmoveEvent: true
  });

  // Attach swipeable to document
  useEffect(() => {
    documentRef(document);
  });

  // ===========================================================================
  // Callback that triggers a modal to be shown when there's not much time left
  // ===========================================================================
  const onTimerUpdated = useCallback(({ remainingPercent }) => {

    if (remainingPercent <= QUIZ_LOW_TIME_THRESHOLD_PERCENT && !showLowTimeAlert && !hasDismissedLowTimeAlert)
    {
      setShowLowTimeAlert(true);
    }

  }, [showLowTimeAlert, hasDismissedLowTimeAlert]);

  const isReviewMode = ctx.status === Status.Review || ctx.quizInfo.endsAt <= new Date();

  useEffect(() => {
    if (
      hasTriggeredCelebration.current ||
      !isReviewMode ||
      ctx.quizInfo.type !== "Quiz" ||
      typeof ctx.quizInfo.totalPoints === "undefined" ||
      typeof ctx.quizInfo.userPoints === "undefined"
    ) return;

    const total = Number(ctx.quizInfo.totalPoints);
    const user = Number(ctx.quizInfo.userPoints);
    if (total <= 0 || isNaN(total) || isNaN(user)) return;

    const pct = (user / total) * 100;

    if (pct >= 90) {
      hasTriggeredCelebration.current = true;
      setCelebrationLevel(2);
      const fireCanon = (x, angle) => confetti({
        particleCount: 120,
        angle,
        spread: 55,
        origin: { x, y: 0.65 },
        colors: ['#FFD700', '#FFA500', '#5C5D50', '#ffffff', '#ffe680'],
      });
      fireCanon(0, 60);
      fireCanon(1, 120);
      setTimeout(() => { fireCanon(0, 60); fireCanon(1, 120); }, 500);
      setTimeout(() => { fireCanon(0, 60); fireCanon(1, 120); }, 1100);
    } else if (pct >= 70) {
      hasTriggeredCelebration.current = true;
      setCelebrationLevel(1);
      confetti({
        particleCount: 80,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#7B7C6A', '#5C5D50', '#B0B0A6', '#ffffff', '#E5E5E1'],
      });
    }
  }, [isReviewMode, ctx.quizInfo.type, ctx.quizInfo.totalPoints, ctx.quizInfo.userPoints]);

  const roomMode = (ctx.quizInfo.config || {}).roomMode === true;

  const horizontalScroll = (ctx.quizInfo.config || {}).horizontalScroll === true;
  const swipeModeEnabled = horizontalScroll && isTouchDevice() && !roomMode;

  let questionContent = null;

  if (swipeModeEnabled)
  {
    questionContent = (ctx.quizInfo.questionList || []).map((question, idx) => {

      const isCurSwipeQuestion = idx === swipeQuestionIdx;
      const style = {
        display: isCurSwipeQuestion ? "block" : "none"
      };

      return (
        <div style={style} key={question.id}>
          <QuizQuestion
            swipeModeEnabled={swipeModeEnabled && idx < ctx.quizInfo.questionList.length - 1}
            question={question}
            questionNumber={idx + 1}
            answerIndexMap={ctx.quizInfo.answerIndexMap}
            color={ctx.quizStyle.colorList[idx % ctx.quizStyle.colorList.length]}
            onAnswerChoiceToggled={onAnswerChoiceToggled}
            onAnswerTextUpdated={onAnswerTextUpdated}
            localAnswerMap={answerChoiceMap}
          />
        </div>
      );
    });
  }
  else
  {
    questionContent = (ctx.quizInfo.questionList || []).map((question, idx) =>
      <QuizQuestion
        key={question.id}
        question={question}
        questionNumber={idx + 1}
        answerIndexMap={ctx.quizInfo.answerIndexMap}
        color={ctx.quizStyle.colorList[idx % ctx.quizStyle.colorList.length]}
        onAnswerChoiceToggled={onAnswerChoiceToggled}
        onAnswerTextUpdated={onAnswerTextUpdated}
        localAnswerMap={answerChoiceMap}
        hideAudio={roomMode}
      />
    );
  }

  let userScore = null;

  if (isReviewMode && ctx.quizInfo.type === "Quiz"
    && typeof ctx.quizInfo.totalPoints !== "undefined"
    && typeof ctx.quizInfo.userPoints !== "undefined")
  {
    let totalPoints = Math.round(Number(ctx.quizInfo.totalPoints) * 100) / 100;
    let userPoints = Math.round(Number(ctx.quizInfo.userPoints) * 100) / 100;

    const celebrationLabel = celebrationLevel === 2
      ? <div className="celebration-label celebration-master">🏆 Mästare!</div>
      : celebrationLevel === 1
      ? <div className="celebration-label">Bra jobbat! 🎉</div>
      : null;

    const scoreClass = `quizUserScore mb-3 pl-2 text-dark score-pop${celebrationLevel === 2 ? " gold-glow" : ""}`;

    userScore = <div>
      {celebrationLabel}
      <div className={scoreClass}>
        <FontAwesomeIcon size='lg' icon={faTasks} />
        <span className="ml-2">
          {ctx.quizInfo.alias || "Ditt resultat"}: {isNaN(userPoints) ? 0 : userPoints} av {isNaN(totalPoints) ? 0 : totalPoints} poäng
        </span>
      </div>
    </div>;
  }

  return (
    <div id="quiz">
      <div id="quizContent" className={`${isReviewMode ? "reviewMode" : ""}`}>

        <Modal show={!isReviewMode && showLowTimeAlert && !hasDismissedLowTimeAlert}>
          <Modal.Header>
            <Modal.Title>
              <FontAwesomeIcon icon={faHourglass2} className="text-quizPurple" />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Det börjar bli dags att skicka in dina svar!
          </Modal.Body>
          <Modal.Footer>
            <Button variant="quizPurple" onClick={() => setHasDismissedLowTimeAlert(true)}>
              OK
            </Button>
          </Modal.Footer>
        </Modal>

        {userScore}
        {questionContent}
        {quizIsActive ? 
          <div>
            <button className="completeQuizBtn"
              onClick={saveQuizAnswers}>
              Skicka in!
            </button>
          </div>
        : null}
      </div>

      {quizIsActive && !roomMode && !isStandingMode
        ? <QuizTimer
            classNames="quizParticipantTimer"
            startedAt={ctx.quizInfo.startedAt}
            endsAt={ctx.quizInfo.endsAt}
            onUpdated={onTimerUpdated}
            onElapsed={saveQuizAnswers}
          />
        : null
      }
    </div>
  );
}